'use client';

import React, { useRef, useState } from 'react';
import Image from 'next/image';
import { MdPhone } from 'react-icons/md';
import { ArrowRight } from 'lucide-react';
import CalendlyModal from '@/components/ui/CalendlyModal';
import Eyebrow from '@/components/ui/Eyebrow';
import Magnetic from '@/components/motion/Magnetic';
import { gsap, SplitText, useGSAP } from '@/components/motion/gsap';
import HeroPlatform, { SCATTER, SCATTER_NARROW } from './HeroPlatform';

/**
 * The hero is the thesis, acted out: five separate systems an optical
 * business runs hang loose beside the headline, out of focus and at odd
 * angles, and scrolling brings them into focus as panels of one workspace.
 * Then one order, #4821, is lit in each of them in turn — the order the rest
 * of the page follows.
 *
 * Three transform layers per window so the three motions never fight:
 *   [data-card]  — the scroll scrub (scatter → workspace slot, blur → sharp)
 *   [data-par]   — pointer parallax + idle float, faded out as it docks
 *   [data-float] — the one-shot intro (fade/scale in)
 *
 * The workspace is laid out by CSS in its FINAL state; the scatter is only an
 * offset from there. So resizing, reduced motion and no-JS all land on a real
 * layout, and the scrub always ends at transform: none.
 */

// A lensometer reticle: rings, a crosshair and the axis scale every
// prescription is written against. It turns into alignment as the page scrolls.
const TICKS = Array.from({ length: 72 }, (_, i) => i * 5);
function Reticle() {
  return (
    <svg data-reticle viewBox="-400 -400 800 800" fill="none" className="size-full" aria-hidden>
      <g stroke="#01a7ff" strokeOpacity="0.22">
        <circle r="150" strokeWidth="1" />
        <circle r="262" strokeWidth="1" strokeDasharray="2 7" />
        <circle r="372" strokeWidth="1" />
        <path d="M-392 0H-40M40 0H392M0-392V-40M0 40V392" strokeWidth="1" />
        {TICKS.map((a) => (
          <path
            key={a}
            d={a % 15 ? 'M0-372V-362' : 'M0-372V-350'}
            strokeWidth={a % 15 ? 1 : 1.5}
            transform={`rotate(${a})`}
          />
        ))}
      </g>
      <g
        fill="#01a7ff"
        fillOpacity="0.55"
        fontSize="13"
        fontFamily="var(--font-mono), monospace"
        textAnchor="middle"
      >
        <text x="0" y="-322">
          90°
        </text>
        <text x="330" y="4.5">
          0°
        </text>
        <text x="-326" y="4.5">
          180°
        </text>
      </g>
    </svg>
  );
}

const HeroSection = ({
  title,
  highlightedText,
  description,
  ctaText = 'Book a call',
  backgroundImage = null,
}) => {
  const root = useRef(null);
  const [isCalendlyOpen, setIsCalendlyOpen] = useState(false);

  const openCalendly = () => {
    if (typeof window !== 'undefined' && window.gtag_report_conversion) {
      window.gtag_report_conversion();
    }
    setIsCalendlyOpen(true);
  };
  const closeCalendly = () => setIsCalendlyOpen(false);

  useGSAP(
    () => {
      const section = root.current;
      if (!section) return;
      const q = gsap.utils.selector(section);
      const mm = gsap.matchMedia();

      mm.add(
        { motion: '(prefers-reduced-motion: no-preference)', fine: '(pointer: fine)' },
        (ctx) => {
          const { motion, fine } = ctx.conditions;
          if (!motion) return;

          const cards = q('[data-card]');
          const pars = q('[data-par]');
          const floats = q('[data-float]');

          // ── Intro, on load ───────────────────────────────────────────
          gsap.set(q('[data-intro]'), { visibility: 'visible' });

          const lines = q('[data-split]').map((el) =>
            SplitText.create(el, {
              type: 'words,chars',
              mask: 'words',
              wordsClass: 'split-word',
              aria: 'auto',
            })
          );

          const intro = gsap.timeline({ defaults: { ease: 'expo.out' } });
          intro
            .from(q('[data-eyebrow]'), { autoAlpha: 0, y: 12, duration: 0.8 }, 0.05)
            .from(
              lines.flatMap((s) => s.chars),
              { yPercent: 115, duration: 1.1, stagger: 0.014 },
              0.1
            )
            .from(
              q('[data-copy-rest] > *'),
              { autoAlpha: 0, y: 18, duration: 1, stagger: 0.08 },
              0.6
            )
            .from(
              floats,
              {
                autoAlpha: 0,
                scale: 0.7,
                y: 40,
                duration: 1.6,
                stagger: { each: 0.09, from: 'random' },
              },
              0.25
            )
            .from(
              q('[data-reticle-in]'),
              { autoAlpha: 0, scale: 0.7, rotation: -50, duration: 2.4, ease: 'power3.out' },
              0
            )
            // the hint's children, not the hint: the scrub owns the hint itself,
            // and a scroll during the intro must still be able to hide it
            .from(q('[data-scroll-hint] > *'), { autoAlpha: 0, y: 10, duration: 0.8 }, 1.3);

          // ── Scatter geometry ─────────────────────────────────────────
          // Offsets from layout (offsetLeft/Top), never from getBoundingClientRect:
          // the card is transformed while this runs and the rect would include it.
          const within = (el) => {
            let x = 0;
            let y = 0;
            let n = el;
            while (n && n !== section) {
              x += n.offsetLeft;
              y += n.offsetTop;
              n = n.offsetParent;
            }
            return { x: x + el.offsetWidth / 2, y: y + el.offsetHeight / 2 };
          };
          // Below lg the copy fills the width, so the loose windows ring it
          // instead of sitting beside it, and are dimmed.
          const narrow = () => section.clientWidth < 1024;
          const scatter = (i) => (narrow() ? SCATTER_NARROW : SCATTER)[i % SCATTER.length];
          const dx = (i, el) => scatter(i).x * section.clientWidth - within(el).x;
          const dy = (i, el) => scatter(i).y * section.clientHeight - within(el).y;

          // ── The scrub ────────────────────────────────────────────────
          const assembled = { p: 0 };
          const tl = gsap.timeline({
            defaults: { ease: 'power3.inOut' },
            scrollTrigger: {
              trigger: section,
              start: 'top top',
              // The section's own height (100svh), not window.innerHeight: on a
              // phone innerHeight changes as the toolbar hides, and a pin whose
              // length changes between refreshes shifts everything below it.
              end: () => `+=${Math.round(section.offsetHeight * 2.6)}`,
              pin: true,
              scrub: 0.8,
              invalidateOnRefresh: true,
              anticipatePin: 1,
            },
          });

          tl.to(
            q('[data-copy]'),
            {
              yPercent: -12,
              autoAlpha: 0,
              scale: 0.96,
              filter: 'blur(8px)',
              duration: 0.6,
              ease: 'power2.in',
            },
            0
          )
            .to(q('[data-scroll-hint]'), { autoAlpha: 0, duration: 0.2 }, 0)
            .to(q('[data-photo]'), { scale: 1.08, duration: 1.2, ease: 'none' }, 0)
            .fromTo(
              q('[data-veil]'),
              { autoAlpha: 0 },
              { autoAlpha: 1, duration: 0.9, ease: 'power1.inOut', immediateRender: true },
              0.1
            )
            .to(assembled, { p: 1, duration: 1.1, ease: 'none' }, 0)
            // the reticle swings onto the axis and centres behind the workspace
            .fromTo(
              q('[data-reticle]'),
              {
                x: () => (narrow() ? 0 : section.clientWidth * 0.24),
                rotation: -38,
                scale: 0.82,
              },
              { x: 0, rotation: 0, scale: 1.12, duration: 1.4, immediateRender: true },
              0
            )
            .fromTo(
              cards,
              {
                x: (i, el) => dx(i, el),
                y: (i, el) => dy(i, el),
                rotation: (i) => scatter(i).r,
                rotationY: () => (narrow() ? 0 : -14),
                rotationX: () => (narrow() ? 0 : 5),
                transformPerspective: 1600,
                scale: (i) => scatter(i).s,
                // depth of field: the far windows start out of focus
                filter: (i) => `blur(${((1 - scatter(i).d) * 2.4).toFixed(1)}px)`,
                autoAlpha: (i) => (narrow() ? 0.5 : 0.72 + scatter(i).d * 0.28),
              },
              {
                x: 0,
                y: 0,
                rotation: 0,
                rotationY: 0,
                rotationX: 0,
                scale: 1,
                filter: 'blur(0px)',
                autoAlpha: 1,
                duration: 1.1,
                stagger: { each: 0.06, from: 'end' },
                immediateRender: true,
              },
              0.1
            )
            .fromTo(
              q('[data-frame]'),
              { autoAlpha: 0, scale: 0.94 },
              { autoAlpha: 1, scale: 1, duration: 0.6, ease: 'power2.out', immediateRender: true },
              0.8
            )
            .to(q('[data-sys]'), { autoAlpha: 0, duration: 0.3 }, 1)
            .fromTo(
              q('[data-rise]'),
              { yPercent: 115 },
              { yPercent: 0, duration: 0.7, stagger: 0.1, ease: 'expo.out', immediateRender: true },
              1.05
            );

          // ── Order #4821 arrives and is lit in every system in turn ───
          const T = 1.8;
          const ons = q('[data-card-on]');
          const dots = q('[data-order-dot]');
          const hits = q('[data-hit]');
          const rows = q('[data-hit-row]');
          tl.fromTo(
            q('[data-order]'),
            { autoAlpha: 0, y: -18 },
            { autoAlpha: 1, y: 0, duration: 0.5, ease: 'power3.out', immediateRender: true },
            T - 0.35
          );
          gsap.set(ons, { autoAlpha: 0, scale: 0 });
          gsap.set([...hits, ...rows], { autoAlpha: 0 });
          gsap.set(dots, { scaleX: 0, transformOrigin: '0% 50%' });
          floats.forEach((card, i) => {
            const at = T + 0.3 + i * 0.34;
            tl.to(hits[i], { autoAlpha: 1, duration: 0.14, ease: 'power2.out' }, at)
              .to(hits[i], { autoAlpha: 0, duration: 0.4, ease: 'power2.in' }, at + 0.22)
              .to(rows[i], { autoAlpha: 1, duration: 0.2 }, at)
              .to(card, { y: -6, duration: 0.16, yoyo: true, repeat: 1, ease: 'power2.out' }, at)
              .to(ons[i], { autoAlpha: 1, scale: 1, duration: 0.25, ease: 'back.out(3)' }, at + 0.1)
              .to(dots[i], { scaleX: 1, duration: 0.3, ease: 'power2.out' }, at);
          });
          tl.fromTo(
            q('[data-next-hint]'),
            { autoAlpha: 0, y: 10 },
            { autoAlpha: 1, y: 0, duration: 0.4, immediateRender: true },
            T + 2.2
          ).to({}, { duration: 0.5 });

          // ── Pointer parallax + idle float, one ticker for all windows ─
          const pointer = { x: 0, y: 0, tx: 0, ty: 0 };
          const onMove = (e) => {
            pointer.tx = (e.clientX / window.innerWidth) * 2 - 1;
            pointer.ty = (e.clientY / window.innerHeight) * 2 - 1;
          };
          if (fine) window.addEventListener('pointermove', onMove, { passive: true });

          const setters = pars.map((el) => ({
            x: gsap.quickSetter(el, 'x', 'px'),
            y: gsap.quickSetter(el, 'y', 'px'),
            r: gsap.quickSetter(el, 'rotation', 'deg'),
          }));
          const tick = (time) => {
            const free = 1 - assembled.p;
            pointer.x += (pointer.tx - pointer.x) * 0.06;
            pointer.y += (pointer.ty - pointer.y) * 0.06;
            setters.forEach((s, i) => {
              const d = 0.4 + scatter(i).d;
              const ph = i * 1.7;
              s.x(free * (pointer.x * 34 * d + Math.cos(time * 0.5 + ph) * 6));
              s.y(free * (pointer.y * 24 * d + Math.sin(time * 0.7 + ph) * 10));
              s.r(free * Math.sin(time * 0.4 + ph) * 1.5);
            });
          };
          gsap.ticker.add(tick);

          return () => {
            gsap.ticker.remove(tick);
            window.removeEventListener('pointermove', onMove);
            lines.forEach((s) => s.revert());
          };
        }
      );

      return () => mm.revert();
    },
    { scope: root }
  );

  return (
    <>
      <section
        ref={root}
        aria-label="Hero"
        className="relative h-[100svh] min-h-[640px] overflow-hidden bg-white motion-reduce:h-auto motion-reduce:min-h-0"
      >
        {/* background photo */}
        {backgroundImage && (
          <div data-photo className="absolute inset-0 z-0">
            <Image src={backgroundImage} alt="" fill className="object-cover object-top" priority />
          </div>
        )}

        {/* readability + blend overlays */}
        <div className="pointer-events-none absolute inset-0 z-[5] bg-gradient-to-r from-white via-white/80 to-white/10" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[5] h-40 bg-gradient-to-t from-white to-transparent" />
        {/* the page's own backdrop, faded in over the photo as the workspace forms */}
        <div
          data-veil
          className="pointer-events-none invisible absolute inset-0 z-[6] bg-[radial-gradient(120%_90%_at_50%_40%,#f3f8fe_0%,#e9f2fc_55%,#ffffff_100%)] motion-reduce:hidden"
        />

        {/* reticle */}
        <div className="pointer-events-none absolute inset-0 z-[7] flex items-center justify-center motion-reduce:hidden">
          <div data-reticle-in data-intro className="size-[min(128vh,118vw)] shrink-0">
            <Reticle />
          </div>
        </div>

        <HeroPlatform />

        <div
          data-copy
          data-intro
          className="container absolute inset-0 z-20 mx-auto flex items-center px-5 pt-16 motion-reduce:relative motion-reduce:inset-auto motion-reduce:pb-12 motion-reduce:pt-32"
        >
          <div className="w-full lg:max-w-[57%]">
            <span data-eyebrow className="mb-6 inline-block">
              <Eyebrow>Complete optical platform</Eyebrow>
            </span>

            <h1 className="display mb-7 text-balance break-words text-[clamp(38px,min(4.7vw,9.4vh),88px)] text-secondary-dark">
              <span data-split className="block">
                {title}
              </span>
              <span data-split className="block text-primary-blue">
                {highlightedText}
              </span>
            </h1>

            <div data-copy-rest>
              <p className="mb-9 max-w-xl text-lg leading-relaxed text-secondary-dark/60">
                {description}
              </p>

              <div className="flex flex-wrap items-center gap-3">
                <Magnetic>
                  <button
                    type="button"
                    onClick={openCalendly}
                    className="group inline-flex items-center gap-2.5 rounded-full bg-primary-blue py-2 pl-2 pr-6 font-semibold text-white shadow-[0_14px_34px_-10px_rgba(1,167,255,0.7)] transition-colors hover:bg-[#0095e6] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-blue focus-visible:ring-offset-2"
                  >
                    <span className="grid size-9 place-items-center rounded-full bg-white/20">
                      <MdPhone size={18} color="#fff" />
                    </span>
                    {ctaText}
                  </button>
                </Magnetic>

                <a
                  href="#how-it-works"
                  className="inline-flex items-center gap-2 rounded-full border border-secondary-dark/15 bg-white/70 px-6 py-3 font-semibold text-secondary-dark backdrop-blur transition-colors hover:bg-white"
                >
                  See how it works
                  <ArrowRight className="size-4" strokeWidth={2} />
                </a>
              </div>
            </div>
          </div>
        </div>

        <div
          data-scroll-hint
          data-intro
          aria-hidden
          className="pointer-events-none absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 flex-col items-center gap-2 max-sm:hidden motion-reduce:hidden [@media(max-height:760px)]:hidden"
        >
          <span className="label text-[10px] text-gray-400">Scroll to bring it into focus</span>
          <span className="relative block h-10 w-px overflow-hidden bg-secondary-dark/10">
            <span className="absolute inset-x-0 top-0 h-1/2 animate-scroll-hint bg-primary-blue" />
          </span>
        </div>
      </section>

      <CalendlyModal isOpen={isCalendlyOpen} onClose={closeCalendly} />
    </>
  );
};

export default HeroSection;
